/**
 * Auth service – wraps Firebase Auth SDK.
 *
 * All side-effects (Firestore profile creation, navigation) live in the
 * screens themselves; this service only handles identity.
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import developerService from "./developer.service";

// Custom action URL — Firebase email links redirect here instead of the default Firebase page
const ACTION_CODE_SETTINGS = {
  url: "https://ateno.co/auth/action",
  handleCodeInApp: false,
};

/**
 * Create a new Firebase Auth account and onboard a developer profile.
 * Returns the Firebase user.
 */
export async function signUpEmail({ name, email, password, company }) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(cred.user, { displayName: name });
  // Send verification email via Firebase — link redirects to ateno.co/auth/action
  sendEmailVerification(cred.user, ACTION_CODE_SETTINGS).catch(() => {});
  await developerService.onboard({ name, email, company });
  return cred.user;
}

/**
 * Sign in with email / password and ensure a developer profile exists.
 * If the account exists in Firebase Auth but has no developer profile
 * (e.g. a mobile app user), the profile is created automatically.
 * Returns the Firebase user.
 */
export async function signInEmail({ email, password, rememberMe = true, name = "", company = "" }) {
  await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
  const cred = await signInWithEmailAndPassword(auth, email, password);
  await developerService.onboard({
    name: name || cred.user.displayName || "",
    email,
    company,
  });
  return cred.user;
}

/**
 * Send a password-reset email via Firebase Auth.
 * Link redirects to ateno.co/auth/action — always resolves (anti-enumeration).
 */
export async function sendPasswordReset(email) {
  try {
    await sendPasswordResetEmail(auth, email, ACTION_CODE_SETTINGS);
  } catch (err) {
    // Swallow user-not-found to prevent account enumeration
    if (err.code === "auth/user-not-found") return;
    throw err;
  }
}

/**
 * Sign in / up with Google and ensure a developer profile exists.
 * Returns the Firebase user.
 */
export async function signInGoogle() {
  const result = await signInWithPopup(auth, googleProvider);
  const user = result.user;
  await developerService.onboard({
    name: user.displayName || "",
    email: user.email || "",
    company: "",
  });
  return user;
}

/**
 * Sign out the current user.
 */
export async function signOut() {
  return firebaseSignOut(auth);
}

const authService = { signUpEmail, signInEmail, signInGoogle, signOut, sendPasswordReset };
export default authService;
